import React, { useEffect, useRef } from 'react'
import { Drawer, List } from '@mui/material'
import { useDispatch } from 'store/store'
import { useNavigate, useLocation } from 'react-router-dom'
import { handleLogout } from 'store/AuthContext/authSlice'
import { formatDate } from 'commons/utils/date'
import { useNotification, Notification } from 'commons/customHooks/useNotifications'
import { usePermissions } from 'commons/customHooks/useAuth'
import { useWindowSize } from 'commons/customHooks/useWindowSize'
import { theme } from 'styles/theme/hav-theme'
import { navItems, SUPPORT_LINK } from './constants'
import { ExitToApp } from '@mui/icons-material'
import logoHeader from 'assets/svg/header/logo-havaianas.svg'
import { URL_CONST } from 'constants/urls'
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js'
import { Icon, Modal, ProfileMenu, Typography } from 'components'
import { Notifications } from './Notifications/Notifications'
import EnvironmentIndicator from './EnvironmentIndicator'

import * as S from './styles'

export const Header: React.FC = () => {
  const appInsights = useAppInsightsContext()
  const [openMenu, setOpenMenu] = React.useState(false)
  const [openSubMenu, setOpenSubMenu] = React.useState(false)
  const [openSupportModal, setOpenSupportModal] = React.useState(false)
  const [openNotificationsDrawer, setOpenNotificationsDrawer] = React.useState(false)
  const [openNotifications, setOpenNotifications] = React.useState(false)
  const [width] = useWindowSize()
  const notifButtonRef = useRef<HTMLDivElement>(null)
  const matchSMScreen = width < theme.breakpoints.lg
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { verifyPermission } = usePermissions()
  const { processUnreadedNotifications, notificationsData, unreadedNotificationsData } =
    useNotification()

  const toggleMenuDrawer = () => {
    setOpenMenu(!openMenu)
    setOpenNotificationsDrawer(false)
    setOpenSubMenu(false)
  }

  const toggleSubMenu = () => {
    setOpenSubMenu(!openSubMenu)
  }

  const toggleNotificationsDrawer = () => {
    if (openNotificationsDrawer) {
      processUnreadedNotifications()
    }
    setOpenNotificationsDrawer(!openNotificationsDrawer)
    setOpenMenu(false)
    setOpenSubMenu(false)
  }
  const toggleNotifications = () => {
    if (openNotifications) {
      processUnreadedNotifications()
    }
    setOpenNotifications(!openNotifications)
  }

  const goHome = () => {
    navigate(URL_CONST.ROOT)
    goHomeHandler()
  }

  const goHomeHandler = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const logout = () => {
    navigate(URL_CONST.LOGIN)
    logoutHandler()
  }

  const logoutHandler = () => {
    appInsights.trackEvent({ name: 'Logout Manual' })
    dispatch(handleLogout())
  }

  const isRouteActive = (route: string) => {
    return route === location.pathname
  }

  const verifyActiveRouteItems = (
    routeItems: Array<{ label: string; route: string }>
  ) => {
    return routeItems.some((value) => isRouteActive(value.route))
  }

  useEffect(() => {
    if (!matchSMScreen) {
      setOpenNotificationsDrawer(false)
      setOpenMenu(false)
    }
  }, [matchSMScreen, setOpenNotificationsDrawer, setOpenMenu])

  const renderButtonsContent = () => (
    <S.ButtonsItemsStyled>
      <S.IconButtonStyled onClick={() => setOpenSupportModal(true)}>
        <Icon icon="support" color={theme.colors.neutral.white} size="20" />
      </S.IconButtonStyled>

      {openNotificationsDrawer ? (
        <S.IconButtonStyled onClick={toggleNotificationsDrawer}>
          <S.CloseIconStyled />
        </S.IconButtonStyled>
      ) : (
        <>
          <S.NotificationsButtonWrapper ref={notifButtonRef}>
            <S.IconButtonStyled
              onClick={matchSMScreen ? toggleNotificationsDrawer : toggleNotifications}
            >
              <Icon icon="notification" color={theme.colors.neutral.white} size="24" />
            </S.IconButtonStyled>
            {!!unreadedNotificationsData.length && (
              <S.NotificationNumberStyled>
                {unreadedNotificationsData.length < 9
                  ? unreadedNotificationsData.length
                  : '9+'}
              </S.NotificationNumberStyled>
            )}
          </S.NotificationsButtonWrapper>
          {openNotifications && (
            <Notifications
              setOpen={setOpenNotifications}
              notifButtonRef={notifButtonRef}
            />
          )}
        </>
      )}

      {!matchSMScreen && (
        <S.ProfileMenuWrapper>
          <ProfileMenu onLogout={logout} />
        </S.ProfileMenuWrapper>
      )}
    </S.ButtonsItemsStyled>
  )

  return (
    <>
      <S.AppBarStyled
        position="fixed"
        height={60}
        sx={{ zIndex: (themeStyle) => themeStyle.zIndex.drawer + 1 }}
      >
        <S.WrapContainerStyled>
          <S.WrapGridContainerStyled>
            <S.RowDiv>
              <S.RowDiv>
                {matchSMScreen && (
                  <S.IconButtonStyled onClick={toggleMenuDrawer}>
                    {openMenu ? <S.CloseIconStyled /> : <S.MenuIconStyled />}
                  </S.IconButtonStyled>
                )}
                {!matchSMScreen && (
                  <S.LogoHeader
                    src={logoHeader}
                    alt="Logo Header Havaianas"
                    onClick={goHome}
                  />
                )}
              </S.RowDiv>
              {!matchSMScreen && (
                <S.RowDiv isNavItems>
                  {Object.keys(navItems).map(function (key, index) {
                    if (!verifyPermission(navItems[key].screen, navItems[key].action))
                      return
                    return (
                      <S.RowDiv key={`row-${index}${key}`}>
                        {navItems[key].item.length > 0 ? (
                          <S.Dropdown>
                            <S.NavItemStyled
                              type={S.TYPES_NAV_ITEM.PRIMARY}
                              key={`label-${index}`}
                              active={verifyActiveRouteItems(
                                navItems[key].item
                              ).toString()}
                              to={''}
                              className="submenu-label"
                            >
                              {navItems[key].label}
                              <S.ChevronDownIconStyled />
                            </S.NavItemStyled>
                            <S.DropdownContent className="dropdown-content">
                              {navItems[key]?.item?.map(
                                ({ route, label, screen }, indexNavItem) => {
                                  if (!verifyPermission(screen)) return
                                  return (
                                    <S.NavItemStyled
                                      type={S.TYPES_NAV_ITEM.SECONDARY}
                                      key={`label-${indexNavItem}`}
                                      to={route}
                                      active={isRouteActive(route).toString()}
                                    >
                                      {label}
                                    </S.NavItemStyled>
                                  )
                                }
                              )}
                            </S.DropdownContent>
                          </S.Dropdown>
                        ) : (
                          <S.NavItemStyled
                            type={S.TYPES_NAV_ITEM.PRIMARY}
                            key={`label-${index}`}
                            to={navItems[key].route}
                            active={isRouteActive(navItems[key].route).toString()}
                          >
                            {navItems[key].label}
                          </S.NavItemStyled>
                        )}
                      </S.RowDiv>
                    )
                  })}
                </S.RowDiv>
              )}
            </S.RowDiv>
            {matchSMScreen && (
              <S.RowDiv>
                <S.LogoHeader
                  src={logoHeader}
                  alt="Logo Header Havaianas"
                  onClick={goHome}
                />
              </S.RowDiv>
            )}

            <S.WrapSubContainer>
              <EnvironmentIndicator />
              {renderButtonsContent()}
            </S.WrapSubContainer>
          </S.WrapGridContainerStyled>
        </S.WrapContainerStyled>
      </S.AppBarStyled>

      <Drawer anchor="left" open={openMenu}>
        <S.WrapDrawerContent width={300}>
          <S.EmptySpaceDivider isSubmenu={openSubMenu} />
          <List disablePadding>
            {Object.keys(navItems).map(function (key, index) {
              if (!verifyPermission(navItems[key].screen, navItems[key].action)) return

              return (
                <React.Fragment key={`column-${index}`}>
                  {!openSubMenu ? (
                    <S.ColumnDiv key={`column-${index}`}>
                      <>
                        {navItems[key]?.item?.length > 0 ? (
                          <>
                            <S.ListItemStyled onClick={toggleSubMenu}>
                              {navItems[key].label}
                              <S.ChevronRightIconStyled />
                            </S.ListItemStyled>
                          </>
                        ) : (
                          <S.DrawerNavItemStyled
                            active={isRouteActive(navItems[key].route).toString()}
                            key={`label-${index}`}
                            to={navItems[key].route}
                          >
                            {navItems[key].label}
                          </S.DrawerNavItemStyled>
                        )}
                      </>
                    </S.ColumnDiv>
                  ) : (
                    <>
                      {navItems[key]?.item?.length > 0 && (
                        <>
                          <S.ColumnDiv key={`column-${index}`}>
                            <S.ListItemStyled onClick={toggleSubMenu} active>
                              <S.ChevronLeftIconStyled />
                              {navItems[key].label}
                            </S.ListItemStyled>

                            {navItems[key]?.item?.map(({ route, label }, innerIndex) => {
                              return (
                                <>
                                  <S.DrawerNavItemStyled
                                    active={isRouteActive(route).toString()}
                                    key={`label-${innerIndex}`}
                                    to={route}
                                  >
                                    {label}
                                  </S.DrawerNavItemStyled>
                                </>
                              )
                            })}
                          </S.ColumnDiv>
                        </>
                      )}
                    </>
                  )}
                </React.Fragment>
              )
            })}
            <S.DividerStyled />
            <S.EmptySpaceDivider />
            <S.DrawerLogoutButton onClick={logout}>
              <ExitToApp sx={{ fontSize: '20px' }} />
              Sair
            </S.DrawerLogoutButton>
          </List>
        </S.WrapDrawerContent>
      </Drawer>

      <Drawer anchor="right" open={openNotificationsDrawer}>
        <S.WrapDrawerContent width={496}>
          <S.DrawerHeaderStyled>
            <S.SideBarTitleStyled>Notificações</S.SideBarTitleStyled>
          </S.DrawerHeaderStyled>
          <S.DividerStyled />
          <S.WrapDrawerNotifications>
            {notificationsData.length > 0 ? (
              notificationsData.map((element: Notification, index) => (
                <div key={`row-${index}`} className="row">
                  <div id="date" key={`row-date-${index}`}>
                    {formatDate(element.create_date, 'DD/MM')}
                  </div>
                  <div id="message" key={`row-message-${index}`}>
                    {element.message}
                  </div>
                </div>
              ))
            ) : (
              <S.EmptyNotificationsStyled className="row">
                <Icon
                  icon="notification-off"
                  color={theme.colors.neutral.white}
                  size="24"
                />
                Sem notificações.
              </S.EmptyNotificationsStyled>
            )}
          </S.WrapDrawerNotifications>
        </S.WrapDrawerContent>
      </Drawer>

      <Modal
        open={openSupportModal}
        setOpen={setOpenSupportModal}
        showAction={false}
        title="Suporte"
        titleSize={22}
        description={
          <Typography design="hav-default-03-text">
            Tem alguma dúvida ou está enfrentando problemas com um pedido ou sobre o
            pedido ou o portal de vendas? Confira abaixo os canais indicados para suporte
            de acordo com o seu perfil:
            <br />
            <br />
            <strong>Representantes de venda:</strong> envie um e-mail relatando o problema
            ocorrido para rosinei@alpargatas.com
            <br />
            <br />
            <strong>Demais vendedores:</strong> acesse o Service Now{' '}
            <S.SupportLink href={SUPPORT_LINK} target="_blank">
              clicando aqui
            </S.SupportLink>
          </Typography>
        }
      />
    </>
  )
}
